import React, { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import API_BASE_URL from '../config';
import './Spectrogram.css';

// Кеш для загруженных форм волн (чтобы не перезагружать при переключении треков)
const waveformCache = new Map();

// IndexedDB для постоянного хранения waveform данных
const DB_NAME = 'waveform-cache';
const DB_VERSION = 1;
const STORE_NAME = 'waveforms';

// Инициализация IndexedDB
async function initDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'trackId' });
      }
    };
  });
}

// Сохранение waveform в IndexedDB
async function saveWaveformToDB(trackId, peaks, duration) {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    // Сэмплируем peaks для экономии места (берем каждый N-й элемент)
    // Для длинных треков это критично - уменьшаем размер данных
    const maxPoints = 2000; // Максимум 2000 точек для экономии места
    const sampleRate = Math.max(1, Math.floor(peaks.length / maxPoints));
    const sampledPeaks = [];
    
    for (let i = 0; i < peaks.length; i += sampleRate) {
      sampledPeaks.push(peaks[i]);
    }
    
    const data = {
      trackId,
      peaks: sampledPeaks,
      originalLength: peaks.length,
      sampleRate,
      duration,
      timestamp: Date.now()
    };
    
    await store.put(data);
    console.log(`💾 Waveform saved to DB for track ${trackId} (${sampledPeaks.length} points from ${peaks.length})`);
  } catch (err) {
    console.warn('Failed to save waveform to DB:', err);
  }
}

// Загрузка waveform из IndexedDB
async function loadWaveformFromDB(trackId) {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(trackId);
    
    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const data = request.result;
        if (data && data.peaks) {
          // Проверяем формат peaks
          let peaks = data.peaks;
          
          // Если это не массив, пытаемся преобразовать
          if (!Array.isArray(peaks)) {
            peaks = Array.from(peaks);
          }
          
          // Фильтруем только валидные числа
          peaks = peaks.filter(p => typeof p === 'number' && !isNaN(p) && isFinite(p));
          
          if (peaks.length > 0) {
            resolve({ 
              peaks, 
              duration: data.duration,
              timestamp: data.timestamp 
            });
          } else {
            // Если peaks невалидны, удаляем запись
            console.warn('Invalid peaks data in IndexedDB, will regenerate');
            resolve(null);
          }
        } else {
          resolve(null);
        }
      };
      request.onerror = () => {
        console.warn('IndexedDB request error:', request.error);
        resolve(null); // Возвращаем null вместо reject для graceful fallback
      };
    });
  } catch (err) {
    console.warn('Failed to load waveform from DB:', err);
    return null;
  }
}

// Получение уникального ID трека (используем track.id или hash от URL)
function getTrackId(track, audioUrl) {
  if (track && track.id) {
    return `track_${track.id}`;
  }
  // Если нет ID, используем hash от URL
  let hash = 0;
  for (let i = 0; i < audioUrl.length; i++) {
    const char = audioUrl.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return `url_${Math.abs(hash)}`;
}

// Senior UI/UX: Сэмплируем peaks чтобы их количество ТОЧНО соответствовало ширине контейнера
// Это гарантирует, что canvas всегда будет нужной ширины, даже для длинных треков
// Принцип: 1 пиксель = 1 точка peak для идеального соответствия
function samplePeaksForContainer(peaks, containerWidth) {
  if (!peaks || !Array.isArray(peaks) || peaks.length === 0) {
    return peaks;
  }
  
  // КРИТИЧНО: На мобильных используем более агрессивное сэмплирование
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  
  // КРИТИЧНО: Количество точек = ширина контейнера (1 пиксель = 1 точка)
  // На мобильных используем точно ширину контейнера, без дополнительных точек
  // Это гарантирует, что canvas никогда не будет шире контейнера
  const targetPoints = isMobile 
    ? Math.floor(containerWidth) // На мобильных точно по ширине контейнера
    : Math.max(Math.floor(containerWidth), 200); // На десктопе минимум 200 точек для детализации
  
  // Если peaks уже меньше или равно targetPoints, возвращаем как есть
  if (peaks.length <= targetPoints) {
    return peaks;
  }
  
  // Сэмплируем peaks равномерно - используем точное вычисление индексов
  const sampleRate = peaks.length / targetPoints;
  const sampledPeaks = [];
  
  // Используем равномерное сэмплирование для точного соответствия
  for (let i = 0; i < targetPoints; i++) {
    const sourceIndex = Math.floor(i * sampleRate);
    if (sourceIndex < peaks.length) {
      sampledPeaks.push(peaks[sourceIndex]);
    }
  }
  
  // Убеждаемся, что точно targetPoints точек (заполняем последними значениями если нужно)
  while (sampledPeaks.length < targetPoints && sampledPeaks.length > 0) {
    sampledPeaks.push(sampledPeaks[sampledPeaks.length - 1]);
  }
  
  // Возвращаем точно targetPoints точек
  return sampledPeaks.slice(0, targetPoints);
}

// Очистка старых данных из IndexedDB (старше 30 дней)
async function cleanupOldWaveforms() {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.openCursor();
    const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
    let deletedCount = 0;

    return new Promise((resolve) => {
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const data = cursor.value;
          if (data.timestamp && data.timestamp < thirtyDaysAgo) {
            cursor.delete();
            deletedCount++;
          }
          cursor.continue();
        } else {
          if (deletedCount > 0) {
            console.log(`🧹 Cleaned up ${deletedCount} old waveform entries from IndexedDB`);
          }
          resolve(deletedCount);
        }
      };
      request.onerror = () => {
        console.warn('Failed to cleanup old waveforms:', request.error);
        resolve(0);
      };
    });
  } catch (err) {
    console.warn('Failed to cleanup old waveforms:', err);
    return 0;
  }
}

// Запускаем очистку старых данных при первой загрузке (один раз)
let cleanupDone = false;
if (!cleanupDone && typeof indexedDB !== 'undefined') {
  cleanupDone = true;
  // Запускаем очистку асинхронно, не блокируя загрузку
  setTimeout(() => {
    cleanupOldWaveforms().catch(() => {
      // Игнорируем ошибки очистки
    });
  }, 5000); // Через 5 секунд после загрузки страницы
}

const Spectrogram = ({ audioRef, currentTime, duration, onSeek, track }) => {
  const containerRef = useRef(null);
  const wavesurferRef = useRef(null);
  const currentTrackUrlRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const progressRef = useRef(0);
  const abortControllerRef = useRef(null);
  const overlayRef = useRef(null);

  // Инициализация WaveSurfer - загружаем форму волны один раз
  useEffect(() => {
    if (!containerRef.current || !track || !track.url) {
      setIsReady(false);
      return;
    }

    const audioUrl = track.url.startsWith('http') 
      ? track.url 
      : `${API_BASE_URL}${track.url.startsWith('/') ? track.url : '/' + track.url}`;

    const trackId = getTrackId(track, audioUrl);

    // Если тот же трек уже загружен и wavesurfer существует, не перезагружаем
    if (currentTrackUrlRef.current === audioUrl && wavesurferRef.current) {
      // Убеждаемся, что wavesurfer еще не уничтожен
      try {
        if (wavesurferRef.current.getDuration() > 0) {
          setIsReady(true); // Форма волны уже загружена
          return;
        }
      } catch (err) {
        // Если wavesurfer уничтожен, продолжаем загрузку
      }
    }

    // КРИТИЧНО: Загружаем peaks из кеша ДО создания WaveSurfer
    // Приоритет проверки в loadWaveform: память > IndexedDB > сервер > генерация
    let cachedPeaks = null;
    let cachedDuration = null;
    
    // Проверяем только кеш в памяти СИНХРОННО (самый быстрый доступ)
    const memoryCachedData = waveformCache.get(audioUrl);
    if (memoryCachedData && memoryCachedData.peaks && Array.isArray(memoryCachedData.peaks)) {
      cachedPeaks = memoryCachedData.peaks;
      cachedDuration = memoryCachedData.duration;
      console.log('📦 Found waveform in memory cache - will render INSTANTLY');
      setIsReady(true);
    } else {
      // Если нет в памяти, помечаем как не готовую
      // Проверка IndexedDB и сервера будет в loadWaveform функции
      setIsReady(false);
    }

    // Отменяем предыдущую загрузку если она еще идет
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Создаем новый AbortController для этой загрузки
    abortControllerRef.current = new AbortController();
    const currentSignal = abortControllerRef.current.signal;

    // Очищаем предыдущий wavesurfer
    if (wavesurferRef.current) {
      try {
        wavesurferRef.current.destroy();
      } catch (err) {
        // Игнорируем ошибки при уничтожении
      }
      wavesurferRef.current = null;
    }

    currentTrackUrlRef.current = audioUrl;
    progressRef.current = 0;

    // Очищаем контейнер
    containerRef.current.innerHTML = '';
    
    // Получаем точные размеры контейнера (с учетом padding и border)
    const containerRect = containerRef.current.getBoundingClientRect();
    const containerWidth = Math.floor(containerRect.width) || Math.floor(containerRef.current.clientWidth) || 300;
    const containerHeight = 72; // Фиксированная высота формы волны

    // Создаем контейнер для waveform
    const waveformContainer = document.createElement('div');
    waveformContainer.className = 'spectrogram-wavesurfer';
    // Устанавливаем точные размеры контейнера
    waveformContainer.style.width = `${containerWidth}px`;
    waveformContainer.style.maxWidth = `${containerWidth}px`;
    waveformContainer.style.height = `${containerHeight}px`;
    waveformContainer.style.maxHeight = `${containerHeight}px`;
    waveformContainer.style.overflow = 'hidden';
    waveformContainer.style.overflowX = 'hidden';
    waveformContainer.style.overflowY = 'hidden';
    waveformContainer.style.boxSizing = 'border-box';
    waveformContainer.style.position = 'relative';
    containerRef.current.appendChild(waveformContainer);

    // Инициализируем WaveSurfer - ТОЛЬКО для визуализации формы волны
    // Senior UI/UX подход: на мобильных ПРИНУДИТЕЛЬНО ограничиваем ширину canvas
    const isMobile = window.innerWidth <= 768;
    
    // КРИТИЧНО: На мобильных устанавливаем minPxPerSec так, чтобы canvas НИКОГДА не был шире контейнера
    // Для длинных треков (до 120 минут) и контейнера 400px: minPxPerSec = 400/(120*60) ≈ 0.056
    // Используем очень маленькое значение для гарантии, что canvas не будет шире контейнера
    const estimatedDuration = 120; // Берем оценку для очень длинных треков (120 минут)
    const mobileMinPxPerSec = isMobile && containerWidth > 0 
      ? Math.max(0.01, containerWidth / (estimatedDuration * 60)) // Для очень длинных треков
      : 0.1;
    
    const wavesurfer = WaveSurfer.create({
      container: waveformContainer,
      waveColor: '#6a6a6a',
      progressColor: 'transparent',
      cursorColor: 'transparent',
      barWidth: 1,
      barRadius: 0.5,
      height: containerHeight,
      normalize: true,
      backend: 'MediaElement',
      mediaControls: false,
      interact: false,
      autoplay: false,
      // КРИТИЧНО: fillParent масштабирует под контейнер
      fillParent: true,
      // КРИТИЧНО: На мобильных используем более агрессивное ограничение
      minPxPerSec: isMobile ? mobileMinPxPerSec : 0.1,
      // КРИТИЧНО: pixelRatio влияет на размер canvas - ограничиваем его
      pixelRatio: 1,
    });

    wavesurferRef.current = wavesurfer;

    // Блокируем все попытки воспроизведения
    const blockPlayback = () => {
      if (wavesurfer.isPlaying()) {
        wavesurfer.pause();
      }
    };

    wavesurfer.play = () => {
      blockPlayback();
      return Promise.resolve();
    };

    // Загружаем waveform с оптимизацией для быстрой загрузки
    const startTime = performance.now();
    
    // Асинхронная функция для загрузки waveform с проверкой всех источников
    async function loadWaveform() {
      // 1. Сначала проверяем кеш в памяти (или используем уже загруженные данные)
      let memoryCachedData = cachedPeaks ? { peaks: cachedPeaks, duration: cachedDuration } : waveformCache.get(audioUrl);
      if (memoryCachedData && memoryCachedData.peaks && Array.isArray(memoryCachedData.peaks)) {
        try {
          // Проверяем что peaks в правильном формате (массив чисел)
          let peaks = memoryCachedData.peaks.filter(p => typeof p === 'number' && !isNaN(p));
          if (peaks.length > 0) {
            // КРИТИЧНО: Сэмплируем peaks под ширину контейнера для длинных треков
            peaks = samplePeaksForContainer(peaks, containerWidth);
            // Загружаем с peaks - WaveSurfer использует их и пропускает декодирование
            await wavesurfer.load(audioUrl, peaks);
            if (currentSignal.aborted) return;
            blockPlayback();
            // Canvas будет зафиксирован в событии 'ready'
            setIsReady(true);
            const loadTime = performance.now() - startTime;
            console.log(`⚡ Waveform loaded from memory cache in ${loadTime.toFixed(0)}ms`);
            return;
          }
        } catch (err) {
          console.warn('Failed to load from memory cache, trying IndexedDB...', err);
        }
      }

      // 2. Проверяем IndexedDB
      const dbData = await loadWaveformFromDB(trackId);
      if (dbData && dbData.peaks && Array.isArray(dbData.peaks) && dbData.peaks.length > 0) {
        try {
          // Проверяем что peaks в правильном формате (массив чисел)
          let peaks = dbData.peaks.filter(p => typeof p === 'number' && !isNaN(p));
          if (peaks.length > 0) {
            // КРИТИЧНО: Сэмплируем peaks под ширину контейнера для длинных треков
            peaks = samplePeaksForContainer(peaks, containerWidth);
            await wavesurfer.load(audioUrl, peaks);
            if (currentSignal.aborted) return;
            blockPlayback();
            // Canvas будет зафиксирован в событии 'ready'
            setIsReady(true);
            // Сохраняем в кеш памяти для быстрого доступа
            waveformCache.set(audioUrl, {
              peaks: peaks,
              duration: dbData.duration,
              timestamp: dbData.timestamp || Date.now()
            });
            const loadTime = performance.now() - startTime;
            console.log(`⚡ Waveform loaded from IndexedDB in ${loadTime.toFixed(0)}ms`);
            return;
          }
        } catch (err) {
          console.warn('Failed to load from IndexedDB, trying server...', err);
        }
      }

      // 3. Проверяем сервер (как на SoundCloud)
      if (track && track.id) {
        try {
          const serverResponse = await fetch(`${API_BASE_URL}/api/tracks/${track.id}/waveform`);
          if (serverResponse.ok) {
            const serverData = await serverResponse.json();
            if (serverData && serverData.peaks && Array.isArray(serverData.peaks) && serverData.peaks.length > 0) {
              try {
                // Проверяем что peaks в правильном формате (массив чисел)
                let peaks = serverData.peaks.filter(p => typeof p === 'number' && !isNaN(p));
                if (peaks.length > 0) {
                  // КРИТИЧНО: Сэмплируем peaks под ширину контейнера для длинных треков
                  peaks = samplePeaksForContainer(peaks, containerWidth);
                  await wavesurfer.load(audioUrl, peaks);
                  if (currentSignal.aborted) return;
                  blockPlayback();
                  // КРИТИЧНО: После загрузки принудительно ограничиваем размер canvas
                  setTimeout(() => {
                    const canvases = waveformContainer.querySelectorAll('canvas');
                    canvases.forEach(canvas => {
                      if (canvas.width > containerWidth) {
                        canvas.style.width = `${containerWidth}px`;
                        canvas.style.maxWidth = `${containerWidth}px`;
                        canvas.style.transform = `scaleX(${containerWidth / canvas.width})`;
                        canvas.style.transformOrigin = 'left top';
                      }
                    });
                  }, 100);
                  setIsReady(true);
                  // Сохраняем во все кеши
                  waveformCache.set(audioUrl, {
                    peaks: peaks,
                    duration: serverData.duration,
                    timestamp: serverData.timestamp || Date.now()
                  });
                  saveWaveformToDB(trackId, peaks, serverData.duration).catch(() => {});
                  const loadTime = performance.now() - startTime;
                  console.log(`⚡ Waveform loaded from server in ${loadTime.toFixed(0)}ms`);
                  return;
                }
              } catch (err) {
                console.warn('Failed to load waveform from server data, will analyze audio...', err);
              }
            }
          }
        } catch (err) {
          console.warn('Failed to fetch waveform from server, will analyze audio...', err);
        }
      }

      // 3. Если кеша нет - анализируем аудио (только один раз!)
      console.log('📊 Analyzing audio waveform (this happens only once per track)...');
      try {
        await wavesurfer.load(audioUrl);
        if (currentSignal.aborted) return;
          blockPlayback();
        // Canvas будет зафиксирован в событии 'ready'
          setIsReady(true);
          const loadTime = performance.now() - startTime;
        console.log(`✅ Waveform analyzed in ${loadTime.toFixed(0)}ms - saving for future use`);
        
        // Сохраняем в кеш для МГНОВЕННОЙ загрузки в будущем (как на SoundCloud)
        try {
          const decodedData = wavesurfer.getDecodedData();
          const duration = wavesurfer.getDuration();
          
          if (decodedData && duration) {
            // Извлекаем peaks из AudioBuffer правильно
            // AudioBuffer имеет channelData - массив каналов (Float32Array)
            let peaks;
          if (decodedData instanceof AudioBuffer) {
            // Берем первый канал (моно) или усредняем все каналы
            const channelData = decodedData.getChannelData(0);
            peaks = Array.from(channelData);
          } else if (Array.isArray(decodedData)) {
            peaks = decodedData;
          } else {
            // Если это уже массив peaks
            peaks = Array.from(decodedData);
          }
          
          // КРИТИЧНО: Сэмплируем peaks под ширину контейнера ПЕРЕД сохранением
          // Это гарантирует, что сохраненные peaks всегда поместятся в контейнер
          const originalPeaks = peaks;
          peaks = samplePeaksForContainer(peaks, containerWidth);
          
          if (peaks && peaks.length > 0) {
              const cacheData = {
                peaks: peaks,
                duration: duration,
                timestamp: Date.now()
              };
              
              // Сохраняем в память
              waveformCache.set(audioUrl, cacheData);
              
              // Сохраняем в IndexedDB (асинхронно, не блокируем UI)
              saveWaveformToDB(trackId, peaks, duration).catch(err => {
                console.warn('Failed to save waveform to IndexedDB:', err);
              });
              
              // КРИТИЧНО: Сохраняем на сервер (как на SoundCloud) - для всех пользователей
              if (track && track.id) {
                fetch(`${API_BASE_URL}/api/tracks/${track.id}/waveform`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                    peaks: peaks,
                    duration: duration,
                    sampleRate: 1
                  })
                })
                .then(res => {
                  if (res.ok) {
                    console.log(`💾 Waveform saved to server - available for all users`);
                  } else {
                    console.warn('Failed to save waveform to server:', res.status);
                  }
                })
                .catch(err => {
                  console.warn('Failed to save waveform to server:', err);
                });
              }
            }
          }
        } catch (err) {
          console.warn('Failed to cache waveform:', err);
        }
      } catch (err) {
        // Игнорируем ошибки отмены
        if (err.name === 'AbortError' || currentSignal.aborted) {
          return;
        }
        console.error('❌ Error loading waveform:', err);
        setIsReady(false);
      }
    }

    // Запускаем загрузку
    loadWaveform();

    // КРИТИЧНО: Блокируем ВСЕ события wavesurfer ДО загрузки, чтобы предотвратить мигание
    // Создаем флаг для блокировки всех обновлений после первой загрузки
    let isLocked = false;
    let canvasFixed = false;
    let fixInterval = null;
    let mutationObserver = null;
    
    // Блокируем все события, которые могут вызвать перерисовку
    const blockAllEvents = () => {
      if (isLocked) return;
      
      // Блокируем все методы wavesurfer
      wavesurfer.seekTo = () => { return; };
      wavesurfer.seek = () => { return; };
      wavesurfer.setTime = () => { return; };
      
      // Блокируем renderer полностью
      if (wavesurfer.renderer) {
        // Полностью блокируем draw после первой загрузки
        const originalDraw = wavesurfer.renderer.draw;
        let drawAllowed = true;
        wavesurfer.renderer.draw = function(...args) {
          if (!drawAllowed) return null;
          // Разрешаем только первую отрисовку
          const result = originalDraw ? originalDraw.apply(wavesurfer.renderer, args) : null;
          // После первой отрисовки блокируем навсегда
          setTimeout(() => { drawAllowed = false; }, 100);
          return result;
        };
        
        // Блокируем все методы обновления
        if (wavesurfer.renderer.updateProgress) {
          wavesurfer.renderer.updateProgress = () => {};
        }
        if (wavesurfer.renderer.render) {
          const originalRender = wavesurfer.renderer.render;
          let renderAllowed = true;
          wavesurfer.renderer.render = function(...args) {
            if (!renderAllowed) return null;
            const result = originalRender ? originalRender.apply(wavesurfer.renderer, args) : null;
            setTimeout(() => { renderAllowed = false; }, 100);
            return result;
          };
        }
        
        // Блокируем все методы, которые могут обновить canvas
        if (wavesurfer.renderer.setWidth) {
          const originalSetWidth = wavesurfer.renderer.setWidth;
          wavesurfer.renderer.setWidth = function(width) {
            // Ограничиваем ширину контейнером
            const maxWidth = containerWidth;
            if (width > maxWidth) {
              return originalSetWidth.call(this, maxWidth);
            }
            return originalSetWidth.call(this, width);
          };
        }
        if (wavesurfer.renderer.setHeight) {
          const originalSetHeight = wavesurfer.renderer.setHeight;
          wavesurfer.renderer.setHeight = function() { return; };
        }
        // Блокируем resize если есть
        if (wavesurfer.renderer.resize) {
          const originalResize = wavesurfer.renderer.resize;
          wavesurfer.renderer.resize = function(width) {
            const maxWidth = containerWidth;
            if (width > maxWidth) {
              return originalResize.call(this, maxWidth);
            }
            return originalResize.call(this, width);
          };
        }
      }
      
      isLocked = true;
    };
    
    // Senior UI/UX: ПРИНУДИТЕЛЬНО упаковываем форму волны в контейнер на мобильных
    // НИКАКИХ ИСКЛЮЧЕНИЙ - форма волны ВСЕГДА точно по ширине контейнера
    const fixCanvasSize = () => {
      const container = containerRef.current;
      if (!container) return;
      
      // КРИТИЧНО: На мобильных используем getBoundingClientRect для ТОЧНОЙ ширины
      const isMobile = window.innerWidth <= 768;
      
      // КРИТИЧНО: Получаем ширину от самого верхнего контейнера (spectrogram-container)
      const topContainer = container.closest('.spectrogram-container');
      const topContainerRect = topContainer ? topContainer.getBoundingClientRect() : container.getBoundingClientRect();
      const currentContainerWidth = Math.floor(topContainerRect.width || container.getBoundingClientRect().width || containerWidth);
            const containerHeight = 72;
            
            // Находим все canvas элементы
            const canvases = waveformContainer.querySelectorAll('canvas');
            canvases.forEach(canvas => {
              // КРИТИЧНО: Получаем реальную ширину canvas (атрибут width, а не CSS width)
              const canvasRealWidth = canvas.width || canvas.getBoundingClientRect().width || canvas.offsetWidth || canvas.scrollWidth;
              
        // КРИТИЧНО: На мобильных ВСЕГДА масштабируем canvas до ширины контейнера
        if (isMobile) {
          // КРИТИЧНО: Для длинных треков принудительно масштабируем canvas
          // Используем scaleX для масштабирования, чтобы canvas точно соответствовал ширине контейнера
          if (canvasRealWidth > 0 && currentContainerWidth > 0) {
            // Если canvas шире контейнера, масштабируем его
            if (canvasRealWidth > currentContainerWidth) {
              const scale = currentContainerWidth / canvasRealWidth;
              canvas.style.setProperty('transform', `scaleX(${scale})`, 'important');
              canvas.style.setProperty('transform-origin', 'left top', 'important');
              // КРИТИЧНО: CSS width должна быть равна реальной ширине canvas (не контейнера!)
              // Это нужно для правильного масштабирования через transform
              canvas.style.setProperty('width', `${canvasRealWidth}px`, 'important');
              console.log(`📱 Mobile: Canvas scaled from ${canvasRealWidth}px to ${currentContainerWidth}px (scale: ${scale.toFixed(3)})`);
            } else {
              // Если canvas уже меньше или равен контейнеру, просто устанавливаем ширину
              canvas.style.setProperty('width', `${currentContainerWidth}px`, 'important');
              canvas.style.setProperty('transform', 'none', 'important');
            }
          } else {
            // Если не можем определить размеры, просто устанавливаем ширину контейнера
            canvas.style.setProperty('width', `${currentContainerWidth}px`, 'important');
            canvas.style.setProperty('transform', 'none', 'important');
          }
          canvas.style.setProperty('object-fit', 'contain', 'important');
        } else {
          // На десктопе используем CSS масштабирование через transform
          if (canvasRealWidth > currentContainerWidth) {
            const scale = currentContainerWidth / canvasRealWidth;
            canvas.style.setProperty('transform', `scaleX(${scale})`, 'important');
            canvas.style.setProperty('transform-origin', 'left top', 'important');
            canvas.style.setProperty('width', `${canvasRealWidth}px`, 'important');
          } else {
            canvas.style.setProperty('transform', 'none', 'important');
            canvas.style.setProperty('width', `${currentContainerWidth}px`, 'important');
          }
        }
        
        // КРИТИЧНО: Принудительно устанавливаем ВСЕ стили для блокировки скролла
        canvas.style.setProperty('max-width', `${currentContainerWidth}px`, 'important');
        canvas.style.setProperty('height', `${containerHeight}px`, 'important');
        canvas.style.setProperty('max-height', `${containerHeight}px`, 'important');
        canvas.style.setProperty('overflow', 'hidden', 'important');
        canvas.style.setProperty('overflow-x', 'hidden', 'important');
        canvas.style.setProperty('overflow-y', 'hidden', 'important');
        canvas.style.setProperty('box-sizing', 'border-box', 'important');
        canvas.style.setProperty('display', 'block', 'important');
        canvas.style.setProperty('position', 'relative', 'important');
        canvas.style.setProperty('clip-path', 'inset(0)', 'important');
        canvas.style.setProperty('transform-origin', 'left top', 'important');
      });
      
      // КРИТИЧНО: Ограничиваем контейнер canvas
      waveformContainer.style.setProperty('width', `${currentContainerWidth}px`, 'important');
      waveformContainer.style.setProperty('max-width', `${currentContainerWidth}px`, 'important');
      waveformContainer.style.setProperty('height', `${containerHeight}px`, 'important');
      waveformContainer.style.setProperty('max-height', `${containerHeight}px`, 'important');
      waveformContainer.style.setProperty('overflow', 'hidden', 'important');
      waveformContainer.style.setProperty('overflow-x', 'hidden', 'important');
      waveformContainer.style.setProperty('overflow-y', 'hidden', 'important');
      
      // КРИТИЧНО: На мобильных ПРИНУДИТЕЛЬНО ограничиваем ВСЕ родительские контейнеры
      if (isMobile) {
        // Ограничиваем spectrogram-wavesurfer-container
        if (container) {
          container.style.setProperty('width', '100%', 'important');
          container.style.setProperty('max-width', `${currentContainerWidth}px`, 'important');
          container.style.setProperty('overflow', 'hidden', 'important');
          container.style.setProperty('overflow-x', 'hidden', 'important');
          container.style.setProperty('overflow-y', 'hidden', 'important');
          container.style.setProperty('box-sizing', 'border-box', 'important');
        }
        
        // Ограничиваем spectrogram-container
        if (topContainer) {
          topContainer.style.setProperty('width', '100%', 'important');
          topContainer.style.setProperty('max-width', `${currentContainerWidth}px`, 'important');
          topContainer.style.setProperty('overflow', 'hidden', 'important');
          topContainer.style.setProperty('overflow-x', 'hidden', 'important');
          topContainer.style.setProperty('overflow-y', 'hidden', 'important');
          topContainer.style.setProperty('box-sizing', 'border-box', 'important');
        }
        
        // Ограничиваем progress-bar-container
        const progressBarContainer = container.closest('.progress-bar-container');
        if (progressBarContainer) {
          progressBarContainer.style.setProperty('width', '100%', 'important');
          progressBarContainer.style.setProperty('max-width', '100%', 'important');
          progressBarContainer.style.setProperty('overflow-x', 'hidden', 'important');
          progressBarContainer.style.setProperty('box-sizing', 'border-box', 'important');
        }
        
        // Ограничиваем boombox-progress-section
        const boomboxSection = container.closest('.boombox-progress-section');
        if (boomboxSection) {
          boomboxSection.style.setProperty('width', '100%', 'important');
          boomboxSection.style.setProperty('max-width', '100%', 'important');
          boomboxSection.style.setProperty('overflow-x', 'hidden', 'important');
          boomboxSection.style.setProperty('box-sizing', 'border-box', 'important');
        }
      }
    };
    
    // Блокируем воспроизведение и фиксируем canvas после загрузки
    wavesurfer.on('ready', () => {
      blockPlayback();
      // Фиксируем canvas сразу
      fixCanvasSize();
      
      // Senior UI/UX: Оптимизированный мониторинг для мобильных устройств
      // Используем requestAnimationFrame вместо setInterval для лучшей производительности
      let animationFrameId = null;
      let lastFixTime = 0;
      const isMobileDevice = window.innerWidth <= 768;
      // На мобильных проверяем ОЧЕНЬ часто для мгновенного исправления
      const FIX_INTERVAL = isMobileDevice ? 50 : 200; // 50мс на мобильных для мгновенной реакции
      
      const scheduleFix = () => {
        const now = Date.now();
        if (now - lastFixTime >= FIX_INTERVAL) {
          fixCanvasSize();
          lastFixTime = now;
        }
        animationFrameId = requestAnimationFrame(scheduleFix);
      };
      
      // Запускаем оптимизированный мониторинг
      fixCanvasSize(); // Фиксируем сразу
      animationFrameId = requestAnimationFrame(scheduleFix);
      
      // Сохраняем ID для очистки
      fixInterval = {
        cancel: () => {
          if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
            animationFrameId = null;
          }
        }
      };
      
      // Используем MutationObserver только для критических изменений (реже)
      if (typeof MutationObserver !== 'undefined') {
        let mutationTimeout = null;
        mutationObserver = new MutationObserver(() => {
          // Дебаунсим изменения - фиксируем только через 100мс после последнего изменения
          if (mutationTimeout) {
            clearTimeout(mutationTimeout);
          }
          mutationTimeout = setTimeout(() => {
            fixCanvasSize();
          }, 100);
        });
        
        // Наблюдаем только за критическими изменениями
        mutationObserver.observe(waveformContainer, {
          childList: true,
          attributes: true,
          attributeFilter: ['style', 'width', 'height'],
          subtree: true,
          attributeOldValue: false // Не сохраняем старые значения для экономии памяти
        });
      }
      
      // КРИТИЧНО: На мобильных добавляем ResizeObserver для отслеживания изменений размера контейнера
      let resizeObserver = null;
      const container = containerRef.current;
      if (isMobileDevice && typeof ResizeObserver !== 'undefined' && container) {
        resizeObserver = new ResizeObserver(() => {
          // При изменении размера контейнера принудительно исправляем canvas
          fixCanvasSize();
        });
        resizeObserver.observe(container);
      }
      
      // КРИТИЧНО: На мобильных также добавляем обработчик изменения размера окна
      let windowResizeHandler = null;
      if (isMobileDevice) {
        windowResizeHandler = () => {
          fixCanvasSize();
        };
        window.addEventListener('resize', windowResizeHandler);
        window.addEventListener('orientationchange', windowResizeHandler);
      }
      
      // Сохраняем обработчики для очистки
      if (resizeObserver) {
        fixInterval.resizeObserver = resizeObserver;
      }
      if (windowResizeHandler) {
        fixInterval.windowResizeHandler = windowResizeHandler;
      }
      
      console.log('✅ Canvas size monitoring started (optimized for mobile)');
    });
    
    // Блокируем все события, которые могут вызвать перерисовку
    wavesurfer.on('play', () => {
      wavesurfer.pause();
      blockPlayback();
    });

    wavesurfer.on('seek', () => {
      // Блокируем seek события
    });
    
    wavesurfer.on('interaction', () => {
      // Блокируем interaction события
    });
    
    wavesurfer.on('redraw', () => {
      // Блокируем redraw события
    });
    
    wavesurfer.on('waveform-ready', () => {
      // Блокируем waveform-ready события
    });

    // Мониторинг
    const monitor = setInterval(blockPlayback, 100);

    return () => {
      clearInterval(monitor);
      // Останавливаем мониторинг canvas
      if (fixInterval) {
        if (typeof fixInterval.cancel === 'function') {
          fixInterval.cancel();
        } else {
          clearInterval(fixInterval);
        }
        // Останавливаем ResizeObserver если есть
        if (fixInterval.resizeObserver) {
          fixInterval.resizeObserver.disconnect();
        }
        // Удаляем обработчики изменения размера окна если есть
        if (fixInterval.windowResizeHandler) {
          window.removeEventListener('resize', fixInterval.windowResizeHandler);
          window.removeEventListener('orientationchange', fixInterval.windowResizeHandler);
        }
        fixInterval = null;
      }
      if (mutationObserver) {
        mutationObserver.disconnect();
        mutationObserver = null;
      }
      // Отменяем загрузку при размонтировании
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (wavesurferRef.current) {
        try {
          wavesurferRef.current.destroy();
        } catch (err) {
          // Игнорируем ошибки
        }
        wavesurferRef.current = null;
      }
    };
  }, [track?.url, track?.id]); // УБИРАЕМ isReady из зависимостей, чтобы избежать перезагрузки

  // Обновление прогресса - ТОЛЬКО через CSS, НЕ трогаем wavesurfer
  // Senior UI/UX: Оптимизация для мобильных - уменьшаем частоту обновлений
  useEffect(() => {
    if (!isReady || !duration || duration === 0) return;

    const progress = Math.max(0, Math.min(1, currentTime / duration));
    
    // Обновляем только если прогресс изменился значительно (чтобы избежать лишних обновлений)
    // На мобильных увеличиваем порог для экономии ресурсов
    const isMobile = window.innerWidth <= 768;
    const threshold = isMobile ? 0.005 : 0.001; // Больший порог на мобильных
    
    if (Math.abs(progress - progressRef.current) < threshold) {
      return;
    }
    
    progressRef.current = progress;

    // Обновляем прогресс ТОЛЬКО через CSS переменную
    // НЕ используем wavesurfer.seekTo - это вызывает мигание
    const container = containerRef.current;
    if (container) {
      // Используем requestAnimationFrame для плавного обновления
      requestAnimationFrame(() => {
      container.style.setProperty('--progress', `${progress * 100}%`);
      });
    }
  }, [currentTime, duration, isReady]);

  // Обработка клика для перемотки (через контейнер)
  const handleClick = (e) => {
    if (!isReady || !duration || !onSeek || !audioRef.current) return;

    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const progress = Math.max(0, Math.min(1, x / rect.width));
    const newTime = progress * duration;

    audioRef.current.currentTime = newTime;
    onSeek(newTime);
  };

  return (
    <div 
      className="spectrogram-container"
      onClick={handleClick}
      style={{ '--progress': `${progressRef.current * 100}%` }}
    >
      <div ref={containerRef} className="spectrogram-wavesurfer-container" />
      {/* Кастомный прогресс-бар поверх формы волны */}
      <div ref={overlayRef} className="spectrogram-progress-overlay" />
      {/* Курсор прогресса */}
      <div className="spectrogram-progress-cursor" />
    </div>
  );
};

export default Spectrogram;
